import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Delete,
  MoreVert,
  FlipToFront,
  FlipToBack,
  ArrowUpward,
  ArrowDownward,
  Settings,
} from '@mui/icons-material';
import type { PostItNote as PostItNoteType, PostItColor } from '@/types/thinkspace';

type PostItNoteProps = {
  element: PostItNoteType;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<PostItNoteType>) => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward: () => void;
  onSendBackward: () => void;
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
};

const COLORS: Record<string, string> = {
  yellow: '#FFF59D',
  pink: '#F8BBD9',
  blue: '#90CAF9',
  green: '#A5D6A7',
  orange: '#FFCC80',
  purple: '#CE93D8',
};

const COLOR_OPTIONS: { name: string; value: PostItColor }[] = [
  { name: 'Yellow', value: 'yellow' },
  { name: 'Pink', value: 'pink' },
  { name: 'Blue', value: 'blue' },
  { name: 'Green', value: 'green' },
  { name: 'Orange', value: 'orange' },
  { name: 'Purple', value: 'purple' },
];

export const PostItNote = ({
  element,
  selected,
  canEdit,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  onDragStart,
}: PostItNoteProps) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingContent, setEditingContent] = useState(false);
  const [title, setTitle] = useState(element.title);
  const [content, setContent] = useState(element.content);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [propsTitle, setPropsTitle] = useState(element.title);
  const [propsContent, setPropsContent] = useState(element.content);
  const [selectedColor, setSelectedColor] = useState<PostItColor>(element.color);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const contentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPropsTitle(element.title);
    setPropsContent(element.content);
    setSelectedColor(element.color);
  }, [element.title, element.content, element.color]);

  const handleSaveProperties = () => {
    onUpdate({ title: propsTitle, content: propsContent, color: selectedColor });
    setPropertiesOpen(false);
  };

  const handleCancelProperties = () => {
    setPropsTitle(element.title);
    setPropsContent(element.content);
    setSelectedColor(element.color);
    setPropertiesOpen(false);
  };

  useEffect(() => {
    setTitle(element.title);
    setContent(element.content);
  }, [element.title, element.content]);

  const handleSaveTitle = () => {
    if (title !== element.title) {
      onUpdate({ title });
    }
    setEditingTitle(false);
  };

  const handleSaveContent = () => {
    if (content !== element.content) {
      onUpdate({ content });
    }
    setEditingContent(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setTitle(element.title);
      setEditingTitle(false);
    }
    if (e.key === 'Enter') {
      handleSaveTitle();
    }
  };

  const handleContentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setContent(element.content);
      setEditingContent(false);
    }
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    if (canEdit) {
      e.stopPropagation();
      setEditingTitle(true);
      setTimeout(() => titleInputRef.current?.focus(), 0);
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    if (canEdit) {
      e.stopPropagation();
      setEditingContent(true);
      setTimeout(() => contentInputRef.current?.focus(), 0);
    }
  };

  const isEditing = editingTitle || editingContent;
  const isDialogOpen = propertiesOpen;

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (canEdit && !isEditing) {
          setPropertiesOpen(true);
        }
      }}
      onMouseDown={canEdit && !isEditing && !isDialogOpen ? onDragStart : undefined}
      onTouchStart={canEdit && !isEditing && !isDialogOpen ? onDragStart : undefined}
      sx={{
        position: 'absolute',
        left: element.position.x,
        top: element.position.y,
        width: element.size.width,
        minHeight: element.size.height,
        backgroundColor: COLORS[element.color] || COLORS.yellow,
        borderRadius: 1,
        boxShadow: selected ? '0 0 0 3px #0f2ccf, 2px 4px 8px rgba(0,0,0,0.2)' : '2px 4px 8px rgba(0,0,0,0.15)',
        cursor: canEdit && !isEditing ? 'grab' : 'default',
        userSelect: 'none',
        touchAction: 'none',
        transition: 'box-shadow 0.2s',
        display: 'flex',
        flexDirection: 'column',
        zIndex: element.zIndex,
        '&:active': canEdit && !isEditing ? { cursor: 'grabbing' } : {},
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderBottom: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        {editingTitle ? (
          <TextField
            inputRef={titleInputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={handleTitleKeyDown}
            variant="standard"
            fullWidth
            sx={{ fontWeight: 600 }}
            InputProps={{ disableUnderline: true, sx: { fontWeight: 600 } }}
          />
        ) : (
          <Typography
            variant="subtitle2"
            onClick={handleTitleClick}
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: canEdit ? 'text' : 'default',
              '&:hover': canEdit ? { backgroundColor: 'rgba(0,0,0,0.05)' } : {},
              borderRadius: 0.5,
              px: 0.5,
              mx: -0.5,
            }}
          >
            {element.title || 'Click to add title'}
          </Typography>
        )}

        {canEdit && selected && !isEditing && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5, flexGrow: 1 }}>
        {editingContent ? (
          <TextField
            inputRef={contentInputRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onBlur={handleSaveContent}
            onKeyDown={handleContentKeyDown}
            variant="standard"
            fullWidth
            multiline
            minRows={3}
            InputProps={{ disableUnderline: true }}
          />
        ) : (
          <Typography
            variant="body2"
            onClick={handleContentClick}
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              cursor: canEdit ? 'text' : 'default',
              '&:hover': canEdit ? { backgroundColor: 'rgba(0,0,0,0.05)' } : {},
              borderRadius: 0.5,
              p: 0.5,
              m: -0.5,
              minHeight: '3em',
            }}
          >
            {element.content || 'Click to add content'}
          </Typography>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); setPropertiesOpen(true); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onBringToFront(); }}>
          <ListItemIcon><FlipToFront fontSize="small" /></ListItemIcon>
          <ListItemText>Bring to Front</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onSendToBack(); }}>
          <ListItemIcon><FlipToBack fontSize="small" /></ListItemIcon>
          <ListItemText>Send to Back</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onBringForward(); }}>
          <ListItemIcon><ArrowUpward fontSize="small" /></ListItemIcon>
          <ListItemText>Bring Forward</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onSendBackward(); }}>
          <ListItemIcon><ArrowDownward fontSize="small" /></ListItemIcon>
          <ListItemText>Send Backward</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Properties Dialog */}
      <Dialog
        open={propertiesOpen}
        onClose={handleCancelProperties}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>Note Properties</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            value={propsTitle}
            onChange={(e) => setPropsTitle(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />

          <TextField
            label="Content"
            value={propsContent}
            onChange={(e) => setPropsContent(e.target.value)}
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>Note Color</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {COLOR_OPTIONS.map((c) => (
                <Box
                  key={c.value}
                  onClick={() => setSelectedColor(c.value)}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: COLORS[c.value],
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: selectedColor === c.value ? '3px solid #0f2ccf' : '2px solid rgba(0,0,0,0.2)',
                    boxShadow: selectedColor === c.value ? '0 0 0 2px #0f2ccf33' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                  title={c.name}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: COLORS[selectedColor],
              borderRadius: 1,
              boxShadow: '2px 4px 8px rgba(0,0,0,0.15)',
            }}
          >
            <Typography gutterBottom variant="caption" color="text.secondary">
              Preview
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {propsTitle || 'Note Title'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {propsContent || 'Note content preview...'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelProperties}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProperties}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
